import React from 'react';
import { Form } from 'formik';
import * as Yup from 'yup';
import {
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Paper,
    styled,
    Tooltip,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LockIcon from '@mui/icons-material/LockOutline';

import { Location } from 'components/location/models/LocationModel';
import { MuiForm } from 'lib/Form';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { ActionDialog } from 'ui/dialogs/ActionDialog';
import { DeepLinkDestinationsOptions, TrackingLinkType } from '../trackingLinks';
import { UTMForm } from './UTMForm';
import { useTrackingLinksForm } from '../hooks/useTrackingLinksForm';

interface IDirtyTrackerProps {
    dirty: boolean;
    onChange: (dirty: boolean) => void;
}

const DirtyTracker = ({ dirty, onChange }: IDirtyTrackerProps): null => {
    React.useEffect(() => {
        onChange(dirty);
    }, [dirty, onChange]);
    return null;
};

const StyleButton = styled(Button)(({ theme }) => ({
    '&.Mui-disabled': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.action.disabled,
        color: theme.palette.grey[500]
    }
}));

const StyledButtonContainer = styled(Grid)(({ theme }) => ({
    position: 'sticky',
    bottom: 0,
    left: 0,
    zIndex: 1000,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    backdropFilter: 'blur(25px)',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2)
}));

const StyledCardActionArea = styled(CardActionArea)(({ theme }) => ({
    height: '100%',
    '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto'
    },
    '&[data-active]': {
        backgroundColor: theme.palette.action.selected,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    '&[data-locked]': {
        color: theme.palette.grey[500]
    }
}));

const PremimuChip = styled(Chip)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.purple.core, 0.1),
    borderColor: alpha(theme.palette.purple.core, 0.25),
    color: theme.palette.purple.core,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: '0.75rem',
    '.MuiChip-icon': {
        fontSize: '0.75rem',
        color: theme.palette.purple.core
    }
}));

const StyledCardContent = styled(CardContent)({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative'
});

const URL_FRIENDLY_REGEX = /^[A-Za-z0-9_.-]+$/;
const URL_FRIENDLY_MESSAGE =
    'Use only letters, numbers, hyphens (-), underscores (_), or dots (.). Avoid spaces and special characters.';

const urlFriendlyString = () =>
    Yup.string().matches(URL_FRIENDLY_REGEX, {
        message: URL_FRIENDLY_MESSAGE,
        excludeEmptyString: true
    });

const getValidationSchema = (selectedType: TrackingLinkType, utmParametersEnabled: boolean) =>
    Yup.lazy((values: any) => {
        const anyUtmFilled =
            utmParametersEnabled &&
            Boolean(
                values?.utmSource ||
                    values?.utmMedium ||
                    values?.utmCampaign ||
                    values?.utmContent ||
                    values?.utmTerm
            );

        const utmCoreField = anyUtmFilled
            ? urlFriendlyString().required('This field is required')
            : urlFriendlyString();

        const shape: Record<string, Yup.AnySchema> = {
            internalTitle: Yup.string().required('This field is required'),
            utmSource: utmCoreField,
            utmMedium: utmCoreField,
            utmCampaign: utmCoreField,
            utmContent: urlFriendlyString(),
            utmTerm: urlFriendlyString()
        };

        if (selectedType === TrackingLinkType.APP_LINK) {
            shape.fallbackUrl = Yup.string()
                .url('Please provide a valid URL')
                .required('This field is required');
        } else if (selectedType === TrackingLinkType.EXTERNAL_LINK) {
            shape.location = Yup.string().required('This field is required');
            shape.webDeepLink = Yup.string().notRequired();
            shape.tableNumber = Yup.number().notRequired();
        }

        return Yup.object().shape(shape);
    });

const initialValues = {
    internalTitle: '',
    fallbackUrl: '',
    location: '',
    webDeepLink: '',
    tableNumber: ''
};

interface ICreateTrackingLinkFormProps {
    open: boolean;
    utmParametersEnabled: boolean;
    locations: Location[];
    appLinksLimitReached: boolean;
    onAppLinkCreated: () => void;
    onSubmit: () => void;
    onClose: () => void;
}

export const CreateTrackingLinkForm = ({
    open,
    locations,
    utmParametersEnabled,
    appLinksLimitReached,
    onAppLinkCreated,
    onClose,
    onSubmit
}: ICreateTrackingLinkFormProps) => {
    const {
        selectedType,
        isSubmitting,
        setIsDirty,
        confirmCloseOpen,
        locationOptions,
        handleClose,
        handleConfirmClose,
        handleCancelClose,
        handleSelectAppLink,
        handleSelectWebLink,
        handleSubmit
    } = useTrackingLinksForm({
        utmParametersEnabled,
        appLinksLimitReached,
        locations,
        onAppLinkCreated,
        onSubmit,
        onClose
    });

    const validationSchema = React.useMemo(
        () => getValidationSchema(selectedType, utmParametersEnabled),
        [selectedType, utmParametersEnabled]
    );

    return (
        <Drawer
            variant="temporary"
            open={open}
            onClose={handleClose}
            anchor="right"
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        width: '600px'
                    }
                }
            }}
        >
            <Paper
                sx={{
                    borderRadius: '0px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    height: '100%',
                    overflow: 'auto',
                    position: 'relative'
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{
                        backdropFilter: 'blur(25px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 20
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2}>
                        <Box>
                            <Typography variant="h6" fontWeight="medium">
                                <b>Create Campaign Link</b>
                            </Typography>
                            <Typography variant="caption">
                                Generate a trackable link for marketing campaigns.
                            </Typography>
                        </Box>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                </Box>
                <MuiForm
                    onSubmit={handleSubmit}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    enableReinitialize
                >
                    {({ isValid, dirty }) => (
                        <Form>
                            <DirtyTracker dirty={dirty} onChange={setIsDirty} />
                            <Grid container spacing={3} sx={{ px: 2, pb: 4 }}>
                                <Grid size={12} container spacing={2}>
                                    <Grid size={6}>
                                        <Tooltip
                                            title={
                                                appLinksLimitReached
                                                    ? "You've reached your limit of 1 dynamic link. Upgrade to Premium to create more."
                                                    : ''
                                            }
                                        >
                                            <Card elevation={0} variant="outlined">
                                                <StyledCardActionArea
                                                    onClick={handleSelectAppLink}
                                                    disabled={appLinksLimitReached}
                                                    data-locked={appLinksLimitReached ? '' : undefined}
                                                    data-active={
                                                        selectedType === TrackingLinkType.APP_LINK
                                                            ? ''
                                                            : undefined
                                                    }
                                                >
                                                    <StyledCardContent>
                                                        {appLinksLimitReached && (
                                                            <Box position="absolute" top={4} right={4}>
                                                                <PremimuChip
                                                                    icon={
                                                                        <LockIcon
                                                                            sx={{ fontSize: '0.625rem' }}
                                                                        />
                                                                    }
                                                                    size="small"
                                                                    label="Premium"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            </Box>
                                                        )}
                                                        <PhoneIphoneOutlinedIcon />
                                                        <Typography variant="body2">
                                                            <b>Dynamic App Link</b>
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Routes to iOS / Android store
                                                        </Typography>
                                                    </StyledCardContent>
                                                </StyledCardActionArea>
                                            </Card>
                                        </Tooltip>
                                    </Grid>
                                    <Grid size={6}>
                                        <Card elevation={0} variant="outlined">
                                            <StyledCardActionArea
                                                onClick={handleSelectWebLink}
                                                data-active={
                                                    selectedType === TrackingLinkType.EXTERNAL_LINK
                                                        ? ''
                                                        : undefined
                                                }
                                            >
                                                <StyledCardContent>
                                                    <LanguageOutlinedIcon />
                                                    <Typography variant="body2">
                                                        <b>Web App Link</b>
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Order on the web
                                                    </Typography>
                                                </StyledCardContent>
                                            </StyledCardActionArea>
                                        </Card>
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            backgroundColor: 'grey.100',
                                            paddingX: 2,
                                            paddingY: 1
                                        }}
                                    >
                                        {selectedType === TrackingLinkType.APP_LINK && (
                                            <>
                                                <Typography variant="body2">
                                                    <b>Smart app routing</b>
                                                </Typography>
                                                <Typography variant="caption">
                                                    This link automatically detects the user&apos;s device and
                                                    redirects to the Apple App Store or Google Play Store.
                                                    Users with the app installed will be deep-linked directly
                                                    into the app.
                                                </Typography>
                                            </>
                                        )}
                                        {selectedType === TrackingLinkType.EXTERNAL_LINK && (
                                            <>
                                                <Typography variant="body2">
                                                    <b>Web ordering link</b>
                                                </Typography>
                                                <Typography variant="caption">
                                                    Sends users to your web ordering domain instead of the
                                                    app. Useful for desktop traffic or guests without the app
                                                    installed.
                                                </Typography>
                                            </>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid size={12}>
                                    <TextFormField
                                        name="internalTitle"
                                        label={<MuiFormLabel required>Link Name (Internal)</MuiFormLabel>}
                                        variant="outlined"
                                        placeholder="e.g. October Newsletter"
                                    />
                                </Grid>
                                {selectedType === TrackingLinkType.APP_LINK && (
                                    <Grid size={12}>
                                        <TextFormField
                                            name="fallbackUrl"
                                            label={<MuiFormLabel required>Desktop Fallback URL</MuiFormLabel>}
                                            variant="outlined"
                                            description="Where to send users who open this link on a desktop computer."
                                            placeholder="e.g. https://www.brandname.com"
                                        />
                                    </Grid>
                                )}
                                {selectedType === TrackingLinkType.EXTERNAL_LINK && (
                                    <>
                                        <Grid size={12}>
                                            <AutocompleteFormField
                                                name="location"
                                                label={<MuiFormLabel required>Location</MuiFormLabel>}
                                                options={locationOptions}
                                                placeholder="Select a location"
                                                textFieldVariant="outlined"
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <AutocompleteFormField
                                                name="webDeepLink"
                                                label={
                                                    <MuiFormLabel>
                                                        Deep Link Destination (Optional)
                                                    </MuiFormLabel>
                                                }
                                                options={DeepLinkDestinationsOptions}
                                                placeholder="Select a web deep link"
                                                textFieldVariant="outlined"
                                            />
                                        </Grid>
                                    </>
                                )}
                                <UTMForm
                                    utmParametersEnabled={utmParametersEnabled}
                                    selectedType={selectedType}
                                />
                            </Grid>
                            <StyledButtonContainer>
                                <StyleButton
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    fullWidth
                                    loading={isSubmitting}
                                    disabled={!isValid || isSubmitting}
                                >
                                    Create Link
                                </StyleButton>
                            </StyledButtonContainer>
                        </Form>
                    )}
                </MuiForm>
            </Paper>
            <ActionDialog
                submitLabel="Yes, Cancel"
                discardLabel="No, don't cancel"
                title="Hold On!"
                content="Are you sure you want to cancel this campaign link creation? All changes you made will be lost."
                open={confirmCloseOpen}
                onConfirm={handleConfirmClose}
                onClose={handleCancelClose}
            />
        </Drawer>
    );
};
